import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { getVoiceSettings, saveVoiceSettings, speak, paymentPhrase, voiceSupported } from "@/lib/voice";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Volume2, VolumeX, EyeOff, Play } from "lucide-react";

const POLL_MS = 20000;
const WS_URL = API.replace(/^http/, "ws") + "/ws/notifications";

export function VoiceAnnouncer() {
  const [settings, setSettings] = useState(getVoiceSettings);
  const [open, setOpen] = useState(false);
  const handled = useRef(new Set());
  const busy = useRef(false);

  const update = (patch) => {
    const s = { ...settings, ...patch };
    setSettings(s);
    saveVoiceSettings(s);
  };

  const announce = useCallback((list) => {
    const pending = list.filter((n) => n.kind === "payment_credited" && !n.announced && !handled.current.has(n.id));
    for (const n of pending) {
      handled.current.add(n.id);
      window.dispatchEvent(new CustomEvent("vp:payment-credited"));
      api.post(`/user/notifications/${n.id}/announced`).then(({ data }) => {
        if (!data.claimed) return;
        const s = getVoiceSettings();
        toast.success(s.privacy ? "Payment received" : `Payment received: ${formatINR(n.amount)}`, { description: "Credited to your wallet" });
        if (s.enabled && !s.privacy) speak(paymentPhrase(n.amount), s.volume);
      }).catch(() => handled.current.delete(n.id));
    }
  }, []);

  const poll = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/user/notifications");
      announce([...data].reverse());
    } catch {
      /* retry on next poll */
    } finally {
      busy.current = false;
    }
  }, [announce]);

  useEffect(() => {
    let ws;
    let retry;
    let delay = 1000;
    let stopped = false;
    const connect = () => {
      const token = localStorage.getItem("vp_token");
      if (!token || stopped) return;
      ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      ws.onopen = () => { delay = 1000; poll(); };
      ws.onmessage = (e) => {
        try { const m = JSON.parse(e.data); if (m.type === "notification") announce([m.data]); } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (stopped) return;
        retry = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
      };
    };
    connect();
    const ping = setInterval(() => { if (ws?.readyState === 1) ws.send("ping"); }, 25000);
    return () => { stopped = true; clearTimeout(retry); clearInterval(ping); ws?.close(); };
  }, [announce, poll]);

  useEffect(() => {
    if (voiceSupported()) window.speechSynthesis.getVoices();
    poll();
    const id = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [poll]);

  const test = () => {
    if (!voiceSupported()) { toast.error("Voice is not supported in this browser"); return; }
    speak(paymentPhrase(1000), settings.volume);
  };

  const Icon = settings.enabled && !settings.privacy ? Volume2 : VolumeX;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="voice-settings-btn" aria-label="Voice alert settings"
        className="fixed bottom-5 right-5 z-40 grid h-12 w-12 place-items-center rounded-full border border-emerald-500/30 bg-[#121a28]/90 text-emerald-300 shadow-lg backdrop-blur hover:bg-emerald-500/15 transition-colors">
        <Icon className="h-5 w-5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121a28] border-white/10 max-w-md" data-testid="voice-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Payment Voice Alerts</DialogTitle>
            <DialogDescription className="text-slate-400">Hear an announcement when an admin credits your wallet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <Row icon={Volume2} title="Voice announcements" desc="Speak credited amounts aloud">
              <Switch checked={settings.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="voice-toggle" />
            </Row>
            <Row icon={EyeOff} title="Privacy mode" desc="Hide amounts & mute voice in public">
              <Switch checked={settings.privacy} onCheckedChange={(v) => update({ privacy: v })} data-testid="voice-privacy-toggle" />
            </Row>
            <div>
              <div className="flex justify-between text-sm text-slate-300"><span>Volume</span><span className="font-mono">{Math.round(settings.volume * 100)}%</span></div>
              <input type="range" min="0" max="1" step="0.05" value={settings.volume} data-testid="voice-volume"
                onChange={(e) => update({ volume: Number(e.target.value) })} className="mt-2 w-full accent-emerald-500" />
            </div>
            <Button onClick={test} data-testid="voice-test-btn" className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">
              <Play className="mr-2 h-4 w-4" /> Test Voice
            </Button>
            <p className="text-xs text-slate-500">Test plays a sample only — it never changes your balance. Browsers may require you to interact with the page once before audio can play.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 text-emerald-400" />
        <div><div className="text-sm font-medium">{title}</div><div className="text-xs text-slate-500">{desc}</div></div>
      </div>
      {children}
    </div>
  );
}
